import {
  autoUpdate,
  flip,
  offset,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
} from "@floating-ui/react";
import { useEffect, useState } from "react";

import { BaseInput } from "./input/base";
import { RadioButtons } from "./radio";

type RadioOptionItem = {
  value: string;
  label: string;
};

type RadioSelectProps = {
  items: RadioOptionItem[];
  defaultValue?: RadioOptionItem["value"];
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  dataTestId?: string;
  compact?: boolean;
};

export const RadioSelect = ({
  items,
  defaultValue,
  value,
  onChange,
  disabled,
  dataTestId,
  compact,
}: RadioSelectProps) => {
  const [currentValue, setCurrentValue] = useState<string | undefined>(
    value || defaultValue,
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (currentValue) onChange(currentValue);
  }, [currentValue]);

  const options = useFloating({
    placement: "bottom-start",
    open,
    onOpenChange: setOpen,
    middleware: [
      offset({ mainAxis: 4, crossAxis: -4 }),
      flip({ fallbackPlacements: ["top-start"] }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const optionsInteractions = useInteractions([
    useDismiss(options.context),
    useFocus(options.context),
  ]);

  return (
    <div
      ref={options.refs.setReference}
      {...optionsInteractions.getReferenceProps()}
      className="flex flex-col"
    >
      <BaseInput
        value={items.find((item) => item.value === currentValue)?.label}
        onRightButtonClick={() => setOpen(!open)}
        rightButtonIcon="regularAngleDown"
        readOnly
        disabled={disabled}
        dataTestId={dataTestId}
        compact={compact}
      />
      {open && (
        <div
          ref={options.refs.setFloating}
          style={options.floatingStyles}
          {...optionsInteractions.getFloatingProps()}
          className="flex flex-col z-modal rounded bg-white ml-[0.4rem] px-[1.6rem] py-[0.8rem] w-[20.7rem] gap-small shadow-dropdownMenu"
        >
          <RadioButtons
            className="text-ui"
            options={items}
            value={currentValue}
            onChange={(v) => {
              setCurrentValue(v);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
};
